const sentinelMaxIdentityApi = process.env.REACT_APP_SENTINEL_MAX_IDENTITY_API;
const sentinelMaxApi = process.env.REACT_APP_SENTINEL_MAX_API;

const DEFAULT_DEVICE_AUTH_POLL_INTERVAL_SECONDS = 5;
const DEFAULT_DEVICE_AUTH_EXPIRES_IN_SECONDS = 600;
const SLOW_DOWN_POLL_INTERVAL_INCREMENT_SECONDS = 5;

type DeviceAuthPollingOptions = {
  intervalSeconds?: number;
  expiresInSeconds?: number;
};

export async function initiateDeviceAuthFlow(clientID: string, scope: string) {
  try {
    const response = await fetch(`${sentinelMaxIdentityApi}/realms/master/protocol/openid-connect/auth/device`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        //'Content-Type': 'application/json; charset=UTF-8',
        Accept: '*/*',
        Connection: 'keep-alive',
      },
      body: new URLSearchParams({
        client_id: clientID,
        scope: scope,
      }).toString(),
      //body: JSON.stringify({
      //  client_id: clientID,
      //  scope: scope,
      //}),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(
        `Status: ${response.status} | Error: ${data.error} | Error Description: ${data.error_description}`,
      );
    }
    return data;
  } catch (err: any) {
    console.log('initiateDeviceAuthFlow Error: ', err.message);
    throw err;
  } finally {
  }
}

export async function getAccessToken(
  grantType: string,
  deviceCode: string,
  clientID: string,
  options: DeviceAuthPollingOptions = {},
) {
  try {
    const expiresAt =
      Date.now() + secondsToMilliseconds(options.expiresInSeconds, DEFAULT_DEVICE_AUTH_EXPIRES_IN_SECONDS);
    let pollingIntervalMs = secondsToMilliseconds(options.intervalSeconds, DEFAULT_DEVICE_AUTH_POLL_INTERVAL_SECONDS);

    while (Date.now() < expiresAt) {
      const response = await fetch(`${sentinelMaxIdentityApi}/realms/master/protocol/openid-connect/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: '*/*',
          Connection: 'keep-alive',
        },
        body: new URLSearchParams({
          grant_type: grantType,
          device_code: deviceCode,
          client_id: clientID,
        }).toString(),
      });

      const data = await response.json();
      if (!response.ok) {
        if (data.error === 'authorization_pending') {
          await sleepUntilNextPoll(pollingIntervalMs, expiresAt);
          continue;
        }

        if (data.error === 'slow_down') {
          pollingIntervalMs += secondsToMilliseconds(SLOW_DOWN_POLL_INTERVAL_INCREMENT_SECONDS);
          await sleepUntilNextPoll(pollingIntervalMs, expiresAt);
          continue;
        }

        if (data.error === 'access_denied') {
          throw new Error('Device authorization was denied.');
        }

        if (data.error === 'expired_token') {
          throw new Error('Device authorization request expired.');
        }

        throw new Error(formatTokenError(response.status, data));
      } else {
        localStorage.setItem('accessToken', data.access_token);
        localStorage.setItem('refreshToken', data.refresh_token);
        return;
      }
    }

    throw new Error('Device was not authorized within the allowed timeframe.');
  } catch (err: any) {
    throw new Error('getAccessToken Error: ' + err.message);
  }
}

function secondsToMilliseconds(seconds: number | undefined, fallbackSeconds = 0): number {
  const value = typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0 ? seconds : fallbackSeconds;
  return value * 1000;
}

async function sleepUntilNextPoll(pollingIntervalMs: number, expiresAt: number): Promise<void> {
  const remainingMs = expiresAt - Date.now();
  if (remainingMs <= 0) return;

  await sleep(Math.min(pollingIntervalMs, remainingMs));
}

async function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function formatTokenError(status: number, data: any): string {
  return `Status: ${status} | Error: ${data.error} | Error Description: ${data.error_description}`;
}

export async function refreshAccessToken(grantType: string, clientID: string, refreshToken: string) {
  try {
    const response = await fetch(`${sentinelMaxIdentityApi}/realms/master/protocol/openid-connect/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: '*/*',
        Connection: 'keep-alive',
      },
      body: new URLSearchParams({
        grant_type: grantType,
        client_id: clientID,
        refresh_token: refreshToken,
      }).toString(),
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(
        `Status: ${response.status} | Error: ${data.error} | Error Description: ${data.error_description}`,
      );
    }
    localStorage.setItem('accessToken', data.access_token);
    localStorage.setItem('refreshToken', data.refresh_token);
    return;
  } catch (err: any) {
    throw new Error('refreshAccessToken Error: ' + err.message);
  }
}

export async function getAuthorizedUser(accessToken: string) {
  try {
    const url = new URL(`${sentinelMaxApi}/api-services/api/me`);
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      credentials: 'omit',
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(
        `Status: ${response.status} | Error: ${data.error} | Error Description: ${data.error_description}`,
      );
    }
    return data;
  } catch (err: any) {
    throw new Error('getAuthorizedUser Error: ' + err.message);
  }
}
